import { DatePicker, Spin, Tooltip as AntTooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import
{
      ArcElement,
      BarElement,
      CategoryScale,
      Chart as ChartJS,
      Filler,
      Legend,
      LinearScale,
      LineElement,
      PointElement,
      Tooltip,
} from "chart.js";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ContentLayoutManagement from "../../layouts/ContentLayoutManagement";
import HeaderContent from "../../layouts/HeaderContent";
import { getOrderReportAsync } from "../../store/reducers/orderSlice";

ChartJS.register(
      ArcElement,
      Tooltip,
      Legend,
      CategoryScale,
      LinearScale,
      BarElement,
      PointElement,
      LineElement,
      Filler,
);

/* ------------------------------------------------------------------ */
/* Theme / palette                                                     */
/* ------------------------------------------------------------------ */

const PROVIDER_COLORS = [
      "#38bdf8",
      "#22c55e",
      "#facc15",
      "#f97316",
      "#a78bfa",
      "#fb7185",
      "#34d399",
];

// Semantic colour per order status (Grafana-style state colours).
const STATUS_META: Record<string, string> = {
      ORDERED: "#3b82f6",
      IN_PROGRESS: "#f59e0b",
      READY_DOWNLOAD: "#06b6d4",
      CLOSED: "#22c55e",
      REJECTED: "#ef4444",
      CANCELED: "#8b5cf6",
};
const DEFAULT_STATUS_COLOR = "#64748b";

const AREA_ACCENT = "#38bdf8";

/* ------------------------------------------------------------------ */
/* Small presentational building blocks                                */
/* ------------------------------------------------------------------ */

const Panel = ({
      title,
      accent = "#38bdf8",
      extra,
      children,
      className = "",
      bodyClassName = "",
}: {
      title: string;
      accent?: string;
      extra?: ReactNode;
      children: ReactNode;
      className?: string;
      bodyClassName?: string;
}) => (
      <div
            className={`rounded-lg border border-[#1f2937] bg-[#111827] overflow-hidden ${className}`}
      >
            <div className="flex items-center justify-between border-b border-[#1f2937] px-4 py-2.5">
                  <div className="flex items-center gap-2">
                        <span
                              className="h-3 w-1 rounded-full"
                              style={{ background: accent }}
                        />
                        <h3 className="text-sm font-medium text-slate-200">{title}</h3>
                  </div>
                  {extra}
            </div>
            <div className={`p-4 ${bodyClassName}`}>{children}</div>
      </div>
);

const StatTile = ({
      label,
      value,
      accent,
      valueColor,
      hint,
}: {
      label: string;
      value: ReactNode;
      accent: string;
      valueColor?: string;
      hint?: string;
}) => (
      <div className="relative overflow-hidden rounded-lg border border-[#1f2937] bg-[#111827] p-4">
            <div className="flex items-start justify-between gap-2">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400 leading-tight">
                        {label}
                  </span>
                  <span
                        className="mt-0.5 h-2 w-2 shrink-0 rounded-full"
                        style={{ background: accent }}
                  />
            </div>
            <div
                  className="mt-3 text-3xl font-bold leading-none"
                  style={{ color: valueColor ?? "#f8fafc" }}
            >
                  {value}
            </div>
            {hint && <div className="mt-1.5 text-[11px] text-slate-500">{hint}</div>}
            <div
                  className="absolute inset-x-0 bottom-0 h-[3px]"
                  style={{ background: accent }}
            />
      </div>
);

const EmptyState = ({ label }: { label: string }) => (
      <div className="flex h-full min-h-[120px] items-center justify-center text-sm text-slate-500">
            {label}
      </div>
);

type Item = { label: string; value: number; color: string };

/* Donut + legend, used for both status and order-type breakdowns. */
const DonutBreakdown = ({
      items,
      centerLabel,
      emptyLabel,
}: {
      items: Item[];
      centerLabel: string;
      emptyLabel: string;
}) =>
{
      const total = items.reduce((s, i) => s + i.value, 0);

      if (total === 0) return <EmptyState label={emptyLabel} />;

      const data = {
            labels: items.map((i) => i.label),
            datasets: [
                  {
                        data: items.map((i) => i.value),
                        backgroundColor: items.map((i) => i.color),
                        borderColor: "#111827",
                        borderWidth: 2,
                        hoverOffset: 4,
                  },
            ],
      };

      return (
            <div className="flex flex-col items-center">
                  <div className="relative h-[150px] w-[150px]">
                        <Doughnut
                              data={data}
                              options={{
                                    maintainAspectRatio: false,
                                    cutout: "72%",
                                    plugins: {
                                          legend: { display: false },
                                          tooltip: {
                                                backgroundColor: "#0b1120",
                                                borderColor: "#1f2937",
                                                borderWidth: 1,
                                                padding: 10,
                                                callbacks: {
                                                      label: (ctx) =>
                                                            ` ${ctx.label}: ${ctx.parsed} (${total > 0
                                                                  ? Math.round((ctx.parsed / total) * 100)
                                                                  : 0
                                                            }%)`,
                                                },
                                          },
                                    },
                              }}
                        />
                        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                              <span className="text-2xl font-bold text-white">{total}</span>
                              <span className="text-[11px] text-slate-400">{centerLabel}</span>
                        </div>
                  </div>

                  <div className="mt-4 w-full space-y-1.5">
                        {items.map((i) =>
                        {
                              const pct = total > 0 ? Math.round((i.value / total) * 100) : 0;
                              return (
                                    <div
                                          key={i.label}
                                          className="flex items-center justify-between text-sm"
                                    >
                                          <span className="flex items-center gap-2 truncate text-slate-300">
                                                <span
                                                      className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                                      style={{ background: i.color }}
                                                />
                                                <span className="truncate">{i.label}</span>
                                          </span>
                                          <span className="shrink-0 tabular-nums text-slate-400">
                                                <b className="text-white">{i.value}</b>
                                                <span className="ml-1 text-slate-500">{pct}%</span>
                                          </span>
                                    </div>
                              );
                        })}
                  </div>
            </div>
      );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function HomePage()
{
      const { t } = useTranslation();
      const dispatch = useDispatch<any>();

      const [loading, setLoading] = useState(false);
      const [reportData, setReportData] = useState<any>(null);
      const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);

      const fetchReport = async (range?: [Dayjs, Dayjs] | null) =>
      {
            setLoading(true);
            try
            {
                  const params: any = { is_user: true };
                  if (range)
                  {
                        params.start_date = range[0].startOf("day").valueOf();
                        params.end_date = range[1].endOf("day").valueOf();
                  }
                  const res = await dispatch(getOrderReportAsync(params)).unwrap();
                  setReportData(res?.data?.data ?? null);
            } catch (_)
            {
            } finally
            {
                  setLoading(false);
            }
      };

      useEffect(() =>
      {
            fetchReport(null);
            // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      /* ---- derived data ---- */
      const byStatus: any[] = reportData?.by_status ?? [];
      const byProvider: any[] = reportData?.by_provider ?? [];
      const byDate: any[] = [...(reportData?.by_date ?? [])].sort((a, b) =>
            String(a.date).localeCompare(String(b.date)),
      );
      const totalOrders: number = reportData?.total_orders ?? 0;

      const statusCount = (name: string) =>
            byStatus.find((s) => s.status_name === name)?.count ?? 0;

      const statusItems: Item[] = byStatus.map((s) => ({
            label: t(s.status_name),
            value: s.count,
            color: STATUS_META[s.status_name] ?? DEFAULT_STATUS_COLOR,
      }));

      const providerItems: Item[] = [...byProvider]
            .sort((a, b) => b.count - a.count)
            .map((p, i) => ({
                  label: p.provider_name,
                  value: p.count,
                  color: PROVIDER_COLORS[i % PROVIDER_COLORS.length],
            }));

      /* peak day (highest count) for a small KPI hint */
      const peak = byDate.reduce(
            (acc, d) => (d.count > acc.count ? d : acc),
            { date: null as string | null, count: 0 },
      );

      /* time-series area chart */
      const areaData = {
            labels: byDate.map((d) => dayjs(d.date).format("DD/MM")),
            datasets: [
                  {
                        data: byDate.map((d) => d.count),
                        borderColor: AREA_ACCENT,
                        borderWidth: 2,
                        fill: true,
                        backgroundColor: (ctx: any) =>
                        {
                              const { chart } = ctx;
                              const { ctx: c, chartArea } = chart;
                              if (!chartArea) return "rgba(56,189,248,0.15)";
                              const g = c.createLinearGradient(
                                    0,
                                    chartArea.top,
                                    0,
                                    chartArea.bottom,
                              );
                              g.addColorStop(0, "rgba(56,189,248,0.35)");
                              g.addColorStop(1, "rgba(56,189,248,0)");
                              return g;
                        },
                        tension: 0.35,
                        pointRadius: 3,
                        pointHoverRadius: 5,
                        pointBackgroundColor: AREA_ACCENT,
                        pointBorderColor: "#0b1120",
                        pointBorderWidth: 2,
                  },
            ],
      };

      /* provider column chart */
      const providerBarData = {
            labels: providerItems.map(
                  (p) => p.label.charAt(0).toUpperCase() + p.label.slice(1),
            ),
            datasets: [
                  {
                        label: t("orders"),
                        data: providerItems.map((p) => p.value),
                        backgroundColor: providerItems.map((p) => p.color),
                        borderRadius: 6,
                        maxBarThickness: 56,
                  },
            ],
      };

      return (
            <ContentLayoutManagement
                  header={<HeaderContent breadcrumb={[{ title: t("Overview") }]} />}
            >
                  <Spin spinning={loading}>
                        <div className="min-h-screen space-y-4 bg-[#0b1120] p-5 text-white">
                              {/* ===== TOOLBAR ===== */}
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                    <h1 className="text-lg font-semibold text-slate-100">
                                          {t("Overview")}
                                    </h1>
                                    <div className="flex items-center gap-2">
                                          <DatePicker.RangePicker
                                                className="!bg-[#111827] !border-[#1f2937] [&_input]:!text-white [&_.ant-picker-separator]:!text-slate-400 [&_.ant-picker-suffix]:!text-slate-400"
                                                value={dateRange}
                                                disabledDate={(current) =>
                                                      current && current > dayjs().endOf("day")
                                                }
                                                onChange={(dates) =>
                                                {
                                                      const range = dates as [Dayjs, Dayjs] | null;
                                                      setDateRange(range);
                                                      fetchReport(range);
                                                }}
                                                allowClear
                                                format="DD/MM/YYYY"
                                                placeholder={[t("From date"), t("To date")]}
                                          />
                                          <AntTooltip title={t("Refresh")}>
                                                <button
                                                      onClick={() => fetchReport(dateRange)}
                                                      className="flex h-8 w-8 items-center justify-center rounded-md border border-[#1f2937] bg-[#111827] text-slate-300 transition-colors hover:border-[#38bdf8] hover:text-[#38bdf8]"
                                                >
                                                      <ReloadOutlined spin={loading} />
                                                </button>
                                          </AntTooltip>
                                    </div>
                              </div>

                              {/* ===== KPI STAT TILES ===== */}
                              {/* <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                                    <StatTile
                                          label={t("Total orders")}
                                          value={totalOrders.toLocaleString("vi-VN")}
                                          accent="#38bdf8"
                                          hint={
                                                peak.date
                                                      ? `${t("Peak")}: ${dayjs(peak.date).format(
                                                            "DD/MM",
                                                      )} · ${peak.count}`
                                                      : undefined
                                          }
                                    />
                                    <StatTile
                                          label={t("ORDERED")}
                                          value={statusCount("ORDERED")}
                                          accent={STATUS_META.ORDERED}
                                          valueColor={STATUS_META.ORDERED}
                                    />
                                    <StatTile
                                          label={t("IN_PROGRESS")}
                                          value={statusCount("IN_PROGRESS")}
                                          accent={STATUS_META.IN_PROGRESS}
                                          valueColor={STATUS_META.IN_PROGRESS}
                                    />
                                    <StatTile
                                          label={t("READY_DOWNLOAD")}
                                          value={statusCount("READY_DOWNLOAD")}
                                          accent={STATUS_META.READY_DOWNLOAD}
                                          valueColor={STATUS_META.READY_DOWNLOAD}
                                    />
                                    <StatTile
                                          label={t("CLOSED")}
                                          value={statusCount("CLOSED")}
                                          accent={STATUS_META.CLOSED}
                                          valueColor={STATUS_META.CLOSED}
                                    />
                                    <StatTile
                                          label={t("REJECTED")}
                                          value={statusCount("REJECTED")}
                                          accent={STATUS_META.REJECTED}
                                          valueColor={STATUS_META.REJECTED}
                                    />
                                    <StatTile
                                          label={t("CANCELED")}
                                          value={statusCount("CANCELED")}
                                          accent={STATUS_META.CANCELED}
                                          valueColor={STATUS_META.CANCELED}
                                    />

                              </div> */}

                              {/* ===== TIME SERIES + STATUS DONUT ===== */}
                              <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
                                    <Panel
                                          title={t("Orders over time")}
                                          className="xl:col-span-2"
                                          accent={AREA_ACCENT}
                                    >
                                          <div className="h-[260px]">
                                                {byDate.length === 0 ? (
                                                      <EmptyState label={t("No data")} />
                                                ) : (
                                                      <Line
                                                            data={areaData}
                                                            options={{
                                                                  maintainAspectRatio: false,
                                                                  plugins: {
                                                                        legend: { display: false },
                                                                        tooltip: {
                                                                              backgroundColor: "#0b1120",
                                                                              borderColor: "#1f2937",
                                                                              borderWidth: 1,
                                                                              padding: 10,
                                                                              callbacks: {
                                                                                    label: (ctx) =>
                                                                                          ` ${ctx.parsed.y} ${t("orders")}`,
                                                                              },
                                                                        },
                                                                  },
                                                                  scales: {
                                                                        x: {
                                                                              ticks: { color: "#64748b" },
                                                                              grid: { color: "#151d2e" },
                                                                              border: { color: "#1f2937" },
                                                                        },
                                                                        y: {
                                                                              beginAtZero: true,
                                                                              ticks: {
                                                                                    color: "#64748b",
                                                                                    precision: 0,
                                                                              },
                                                                              grid: { color: "#151d2e" },
                                                                              border: { color: "#1f2937" },
                                                                        },
                                                                  },
                                                            }}
                                                      />
                                                )}
                                          </div>
                                    </Panel>

                                    <Panel title={t("By status")} accent="#22c55e">
                                          <DonutBreakdown
                                                items={statusItems}
                                                centerLabel={t("orders")}
                                                emptyLabel={t("No data")}
                                          />
                                    </Panel>
                              </div>

                              {/* ===== PROVIDER COLUMN CHART ===== */}
                              <Panel title={t("By provider")} accent="#a78bfa">
                                    <div className="h-[300px]">
                                          {providerItems.length === 0 ? (
                                                <EmptyState label={t("No data")} />
                                          ) : (
                                                <Bar
                                                      data={providerBarData}
                                                      options={{
                                                            maintainAspectRatio: false,
                                                            plugins: {
                                                                  legend: { display: false },
                                                                  tooltip: {
                                                                        backgroundColor: "#0b1120",
                                                                        borderColor: "#1f2937",
                                                                        borderWidth: 1,
                                                                        padding: 10,
                                                                        callbacks: {
                                                                              label: (ctx) =>
                                                                                    ` ${ctx.parsed.y} ${t("orders")}`,
                                                                        },
                                                                  },
                                                            },
                                                            scales: {
                                                                  x: {
                                                                        ticks: { color: "#94a3b8" },
                                                                        grid: { display: false },
                                                                        border: { color: "#1f2937" },
                                                                  },
                                                                  y: {
                                                                        beginAtZero: true,
                                                                        ticks: {
                                                                              color: "#64748b",
                                                                              precision: 0,
                                                                        },
                                                                        grid: { color: "#151d2e" },
                                                                        border: { color: "#1f2937" },
                                                                  },
                                                            },
                                                      }}
                                                />
                                          )}
                                    </div>
                              </Panel>
                        </div>
                  </Spin>
            </ContentLayoutManagement>
      );
}
